// src/services/demoApi/markdownBlocks.ts
// Общий разбор markdown-тела секции в структурные блоки для генераторов
// документов (docx/pptx/pdf/xlsx). Вместо вырезания разметки переводит её
// в раны (bold/italic/code), списки, таблицы и цитаты.

export interface InlineRun {
  text: string;
  bold?: boolean;
  italic?: boolean;
  code?: boolean;
}

export type MdBlock =
  | { type: 'paragraph'; runs: InlineRun[] }
  | { type: 'bullets'; items: InlineRun[][] }
  | { type: 'numbered'; items: InlineRun[][] }
  | { type: 'table'; headers: string[]; rows: string[][] }
  | { type: 'quote'; runs: InlineRun[] };

const INLINE_TOKEN_RE = /\*\*([^*]+)\*\*|__([^_]+)__|`([^`]+)`|\*([^*\s][^*]*?)\*|_([^_\s][^_]*?)_/;

/** Ссылки [текст](url) → текст; выполняется до разбора ранов */
function stripLinks(line: string): string {
  return line.replace(/\[([^\]]+)\]\([^)]*\)/g, '$1');
}

/** Разбор инлайн-разметки строки в раны: **жирный**, *курсив*, `код` */
export function parseInlineRuns(line: string): InlineRun[] {
  const runs: InlineRun[] = [];
  let rest = stripLinks(line);
  while (rest.length > 0) {
    const match = INLINE_TOKEN_RE.exec(rest);
    if (!match) {
      runs.push({ text: rest });
      break;
    }
    if (match.index > 0) runs.push({ text: rest.slice(0, match.index) });
    const [, bold, bold2, code, italic, italic2] = match;
    if (bold !== undefined || bold2 !== undefined) runs.push({ text: bold ?? bold2, bold: true });
    else if (code !== undefined) runs.push({ text: code, code: true });
    else runs.push({ text: italic ?? italic2, italic: true });
    rest = rest.slice(match.index + match[0].length);
  }
  return runs.filter((run) => run.text.length > 0);
}

/** Плоский текст ранов (для ячеек таблиц и фолбэков) */
export function runsToPlainText(runs: InlineRun[]): string {
  return runs.map((run) => run.text).join('');
}

function parseTableRow(line: string): string[] {
  const trimmed = line.trim().replace(/^\|/, '').replace(/\|$/, '');
  return trimmed.split('|').map((cell) => runsToPlainText(parseInlineRuns(cell.trim())));
}

function isTableSeparator(line: string): boolean {
  return /^\|?\s*:?-{2,}.*\|/.test(line.trim()) && /^[\s|:-]+$/.test(line.trim());
}

function isTableRow(line: string): boolean {
  const trimmed = line.trim();
  return trimmed.startsWith('|') && trimmed.includes('|', 1);
}

/** Markdown-тело → последовательность блоков */
export function parseBlocks(md: string): MdBlock[] {
  const lines = md.split('\n');
  const blocks: MdBlock[] = [];
  let paragraph: string[] = [];

  const flushParagraph = () => {
    const text = paragraph.join(' ').trim();
    if (text) blocks.push({ type: 'paragraph', runs: parseInlineRuns(text) });
    paragraph = [];
  };

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (!trimmed) {
      flushParagraph();
      i += 1;
    } else if (isTableRow(trimmed) && i + 1 < lines.length && isTableSeparator(lines[i + 1])) {
      flushParagraph();
      const headers = parseTableRow(trimmed);
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && isTableRow(lines[i].trim())) {
        const row = parseTableRow(lines[i].trim());
        // Выравниваем длину строки под шапку
        rows.push(headers.map((_, col) => row[col] ?? ''));
        i += 1;
      }
      blocks.push({ type: 'table', headers, rows });
    } else if (/^[-*+]\s+/.test(trimmed)) {
      flushParagraph();
      const items: InlineRun[][] = [];
      while (i < lines.length && /^[-*+]\s+/.test(lines[i].trim())) {
        items.push(parseInlineRuns(lines[i].trim().replace(/^[-*+]\s+/, '')));
        i += 1;
      }
      blocks.push({ type: 'bullets', items });
    } else if (/^\d+[.)]\s+/.test(trimmed)) {
      flushParagraph();
      const items: InlineRun[][] = [];
      while (i < lines.length && /^\d+[.)]\s+/.test(lines[i].trim())) {
        items.push(parseInlineRuns(lines[i].trim().replace(/^\d+[.)]\s+/, '')));
        i += 1;
      }
      blocks.push({ type: 'numbered', items });
    } else if (trimmed.startsWith('>')) {
      flushParagraph();
      const quoted: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith('>')) {
        quoted.push(lines[i].trim().replace(/^>\s?/, ''));
        i += 1;
      }
      blocks.push({ type: 'quote', runs: parseInlineRuns(quoted.join(' ')) });
    } else {
      paragraph.push(trimmed);
      i += 1;
    }
  }
  flushParagraph();
  return blocks;
}

/** Первая markdown-таблица из тела (для xlsx-документов агента) */
export function extractFirstTable(md: string): { headers: string[]; rows: string[][] } | null {
  const table = parseBlocks(md).find((block) => block.type === 'table');
  return table && table.type === 'table' ? { headers: table.headers, rows: table.rows } : null;
}
